import CheckCircle from "@mui/icons-material/CheckCircle";
import RemoveCircle from "@mui/icons-material/RemoveCircle";
import { type GridRowSelectionModel } from "@mui/x-data-grid-pro";
import axios, { type AxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import {
	emptyGridRowSelectionModel, gridRowSelectionIdsArray,
	gridRowSelectionSize, resolveGridSelectedRowIds
} from "../../../helpers/dataGridRowSelectionModel";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useDatagridSorting from "../../../hooks/useDatagridSorting";
import useToastAlert from "../../../hooks/useToastAlert";
import CoAppDatagridHeader from "../../global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "../../global/components/datagrid/CoAppStandardDataGrid";
import useCoAppDataGridFilters from "../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppKillswitchModal from "../../global/components/modals/CoAppKillswitchModal";
import SettingsDataGridSelectionRow from "../SettingsDataGridSelectionRow";
import { getWorkstationManagementColumns, workstationColumns, type WorkstationRow } from "./columns/workstation-management-columns";

interface Workstation {
	uuid: string;
	killSwitchActive: boolean;
	[key: string]: unknown;
}

export default function WorkstationManagement() {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("workstations");
	const [workstationsSelected, setWorkstationsSelected] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [workstationKillswitchDialogOpen, setWorkstationKillswitchDialogOpen] = useState<boolean>(false);
	const [workstations, setWorkstations] = useState<Workstation[]>([]);
	const [workstationsForDisplayInModal, setWorkstationsForDisplayInModal] = useState<Workstation[]>([]);
	const [activateIsDisabled, setActivateIsDisabled] = useState<boolean>(false);
	const [deactivateIsDisabled, setDeactivateIsDisabled] = useState<boolean>(false);
	const [actionType, setActionType] = useState<"Activate" | "Deactivate">("Activate");
	const [loading, setLoading] = useState<boolean>(true);
	const [workstationIdFilterSet, setWorkstationIdFilterSet] = useState<boolean>(false);

	const handleKillSwitchModalToggle = useCallback((workstation: Workstation | null = null, isDeactivate: boolean = false) => {
		if (workstation) {
			setWorkstationsForDisplayInModal([workstation]);
			setWorkstationKillswitchDialogOpen(true);
			setActionType(isDeactivate ? "Deactivate" : "Activate");
		} else {
			const workstationsForDisplay: Workstation[] = [];
			const selectedUuids = gridRowSelectionIdsArray(workstationsSelected, workstations) as string[];
			selectedUuids.forEach((selectedUUID) => {
				const workstation = workstations.filter(workstation => {
					if (isDeactivate) {
						return selectedUUID === workstation.uuid && workstation.killSwitchActive;
					} else {
						return selectedUUID === workstation.uuid && !workstation.killSwitchActive;
					}
				});
				if (workstation.length > 0) {
					workstationsForDisplay.push(workstation[0]);
				}
			});
			setWorkstationsForDisplayInModal(workstationsForDisplay);
			setWorkstationKillswitchDialogOpen(true);
			setActionType(isDeactivate ? "Deactivate" : "Activate");
		}
	}, [workstationsSelected, workstations]);

	const handleKillSwitchFromGridRow = useCallback((row: WorkstationRow, killSwitchCurrentlyActive: boolean) => {
		handleKillSwitchModalToggle(
			{
				...row,
				uuid: typeof row.uuid === "string" ? row.uuid : String(row.id),
				killSwitchActive: Boolean(row.killSwitchActive),
			},
			killSwitchCurrentlyActive,
		);
	}, [handleKillSwitchModalToggle]);

	// Stable column defs — new arrays on each render make MUI X discard autosized widths.
	const columns = useMemo(
		() => getWorkstationManagementColumns(handleKillSwitchFromGridRow),
		[]
	);

	const initWorkstationManagement = () => {
		axios.get(apiRoutes.getWorkstations, { headers: authHeader })
			.then((res) => {
				setWorkstations(res.data);
				setLoading(false);
				// if there is a workstationId in the url, apply filter to datagrid
				const urlParams = new URLSearchParams(window.location.search);
				const workstationId = urlParams.get("workstationId");
				if (workstationId) {
					const filteredWorkstations = res.data.filter((workstation: Workstation) => workstation.uuid === workstationId);
					setWorkstations(filteredWorkstations);
					setWorkstationIdFilterSet(true);
				}
			}).catch((err: AxiosError) => {
				console.log(err);
			});
	};

	const handleBulkWorkstationStatusToggle = () => {
		const workstationJSON = {
			newStatus: actionType === "Deactivate" ? false : true,
			workstationUUIDs: workstationsForDisplayInModal.map(workstation => workstation.uuid),
		};

		axios.put(apiRoutes.bulkWorkstationKillSwitchToggle, workstationJSON, {
			headers: authHeader
		}).then(() => {
			initWorkstationManagement();
			setWorkstationKillswitchDialogOpen(false);
			setWorkstationsSelected(emptyGridRowSelectionModel());
		})
			.catch((error: AxiosError) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.WORKSTATION_STATUS_TOGGLE_ERROR_MSG);
			});
	};

	const handleWorkstationFilterClear = () => {
		setWorkstationIdFilterSet(false);
		initWorkstationManagement();
		try {
			const urlParams = new URLSearchParams(window.location.search);
			urlParams.delete("workstationId");
			window.history.replaceState({}, "", window.location.pathname + "?" + urlParams.toString());
		} catch (error) {
			console.error(error);
		}
	};

	const handleWorkstationStatusToggle = () => {
		if (workstationsForDisplayInModal.length === 0) return;
		
		axios.put(apiRoutes.workstationKillSwitch(workstationsForDisplayInModal[0].uuid), { newStatus: actionType === "Deactivate" ? false : true }, { headers: authHeader })
			.then(() => {
				initWorkstationManagement();
				setWorkstationKillswitchDialogOpen(false);
			})
			.catch((error: AxiosError) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.WORKSTATION_STATUS_TOGGLE_ERROR_MSG);
			});
	};

	const handleSelectionModelChange = (newSelectionModel: GridRowSelectionModel) => {
		const ids = gridRowSelectionIdsArray(newSelectionModel, workstations) as string[];
		const fullWorkstations = workstations.filter(workstation => ids.includes(workstation.uuid));
		const hasActiveKillSwitches = fullWorkstations.some(workstation => workstation.killSwitchActive);
		const hasInactiveKillSwitches = fullWorkstations.some(workstation => !workstation.killSwitchActive);

		if (!hasActiveKillSwitches) {
			setDeactivateIsDisabled(true);
		} else {
			setDeactivateIsDisabled(false);
		}
		if (!hasInactiveKillSwitches) {
			setActivateIsDisabled(true);
		} else {
			setActivateIsDisabled(false);
		}
		setWorkstationsSelected(newSelectionModel);
	};

		/**
     * Update the row in the database, only pass the fields that are updatable
     * @param newRow - The new row data
     * @returns The row data for the grid to update the row
     * Note: If the dataFieldType is not an object aka the user has not selected a data type but tries to save,
     * we return the old row to prevent the grid from attempting to save the row
     */
	const handleRowUpdate = async (newRow, oldRow) => {
		if (typeof newRow.comment !== "string") return oldRow;
		const dataToUpdate = {
			comment: newRow.comment,
		};

		try {
			const res = await axios.patch(apiRoutes.patchWorkstation(newRow.uuid), { data: dataToUpdate }, { headers: authHeader });
			if (res.status !== 200) {
				throw new Error(messages.FIELD_UPDATE_FAILED_ERROR_MSG);
			}
		} catch (err) {
			handleToastAlert(messageLevels.ERROR, messages.FIELD_UPDATE_FAILED_ERROR_MSG);
		}
		return newRow;
	};

	useEffect(() => {
		initWorkstationManagement();
		handleInitializeSortModel();
	}, []);

	const { gridFilterModel, onFilterModelChange, quickFilterSearchProps, apiRef, visibleRowCount, filterControl, filterButtonEl } = useCoAppDataGridFilters(workstations, loading);
	const selectedWorkstationCount = resolveGridSelectedRowIds(workstationsSelected, workstations).length;

	return (
		<>
			<CoAppDatagridHeader
				iconKind="workstations"
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				title="Workstations"
				addIsPresent={false}
				editIsPresent={false}
				deleteIsPresent={false}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				columns={columns}
				rows={workstations}
				allowSelection={true}
				targetResource="workstations"
				gridKey="workstations"
				selectionModel={workstationsSelected}
				setSelectionModel={handleSelectionModelChange}
				customSelectionChangeHandler={handleSelectionModelChange}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				idFilterSet={workstationIdFilterSet}
				clearFilterHandler={handleWorkstationFilterClear}
				processRowUpdate={handleRowUpdate}
				selectionFooter={
					<SettingsDataGridSelectionRow
						resourceNoun="workstation"
						visibleCount={visibleRowCount}
						selectedCount={selectedWorkstationCount}
						loading={loading}
						onClearSelection={() => setWorkstationsSelected(emptyGridRowSelectionModel())}
						actions={[
							{
								label: "Activate kill switch",
								icon: <CheckCircle sx={{ fontSize: 16 }} />,
								onClick: () => handleKillSwitchModalToggle(null, false),
								disabled: activateIsDisabled,
								variant: "primary",
							},
							{
								label: "Deactivate kill switch",
								icon: <RemoveCircle sx={{ fontSize: 16 }} />,
								onClick: () => handleKillSwitchModalToggle(null, true),
								disabled: deactivateIsDisabled,
								variant: "primary",
							},
						]}
					/>
				}
			/>
			<CoAppKillswitchModal
				dialogOpen={workstationKillswitchDialogOpen}
				dialogTitle={gridRowSelectionSize(workstationsSelected, workstations) > 1 ? `${actionType} kill switch on selected workstations?` : `${actionType} workstation kill switch?`}
				dialogMessage={messages.WORKSTATION_KILLSWITCH_MSG(gridRowSelectionSize(workstationsSelected, workstations), workstationsForDisplayInModal[0]?.killSwitchActive)}
				confirmClickHandler={
					workstationsForDisplayInModal.length > 1 ?
						() => handleBulkWorkstationStatusToggle()
						:
						() => handleWorkstationStatusToggle()
				}
				cancelClickHandler={() => setWorkstationKillswitchDialogOpen(false)}
				actionText={`${actionType} Kill Switch`}
				workstations={workstationsForDisplayInModal}
				isOrganizationWide={false}
			/>
		</>
	);
}